// Spool Board — kanban grouped by current stage. Every spool across
// every visible job becomes a card in the column matching its
// current stage; spools with no remaining work fall into a "Done"
// column. Click a card to open the job modal; drag a card to a
// different stage column to advance / regress the spool through
// the workflow.
import { useMemo, useState } from "react";
import type {
  JobDto,
  SpoolDto,
  StageDefDto,
  StageStatus,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "../components/StagesProvider";
import { patchSpoolStage } from "../hooks/useProductionApi";
import {
  activeStages,
  currentStage,
  denseStages,
  inferWeldBayKey,
  isSpoolComplete,
  spoolOverallStatus,
} from "../lib/stageHelpers";
import { fmtDate, isoToday } from "../lib/dates";

interface Props {
  jobs: JobDto[];
  // FilterBar's 'All Active' default — skip spools whose own
  // progression stages are complete. Read inside the view so the
  // catalogue from StagesProvider drives isSpoolComplete.
  activeOnly?: boolean;
  onOpenJob(id: number, spoolId: number): void;
  onSpoolUpdated?(): void | Promise<void>;
}

interface Row {
  job: JobDto;
  spool: SpoolDto;
}

interface Column {
  key: string;
  label: string;
  color: string;
  rows: Row[];
}

const DONE_KEY = "_done";
const UNSCHEDULED_KEY = "_unscheduled";

function bucket(rows: Row[], catalogue: StageDefDto[]): Column[] {
  const byStage = new Map<string, Row[]>();
  const done: Row[] = [];
  for (const r of rows) {
    const bay = inferWeldBayKey(r.spool.stages, r.job.bay);
    const stages = denseStages(r.spool.stages, bay, undefined, catalogue);
    if (spoolOverallStatus(stages) === "complete") {
      done.push(r);
      continue;
    }
    const cur = currentStage(stages);
    const key = cur?.stageKey ?? UNSCHEDULED_KEY;
    const arr = byStage.get(key) ?? [];
    arr.push(r);
    byStage.set(key, arr);
  }
  // Always render every active catalogue stage as a column so empty
  // ones still accept drops. "Unscheduled" only shows when there's
  // a spool sitting there; Done always renders so finished spools
  // can be dragged in.
  const cols: Column[] = [];
  const unscheduled = byStage.get(UNSCHEDULED_KEY) ?? [];
  if (unscheduled.length > 0) {
    cols.push({
      key: UNSCHEDULED_KEY,
      label: "No active stages",
      color: "#9CA09F",
      rows: unscheduled,
    });
  }
  for (const def of catalogue) {
    cols.push({
      key: def.stageKey,
      label: def.name,
      color: def.color,
      rows: byStage.get(def.stageKey) ?? [],
    });
  }
  cols.push({
    key: DONE_KEY,
    label: "Done",
    color: "#2E7D32",
    rows: done,
  });
  return cols;
}

// Build the patch list that moves a spool to a given target stage:
// stages before target → complete, target → in-progress, later
// stages with progress get reset to pending. Skipped stages stay
// skipped so non-chosen welding bays don't get woken up.
function buildAdvancePatches(
  spool: SpoolDto,
  jobBay: number | null,
  targetKey: string,
  catalogue: StageDefDto[],
  todayIso: string,
): { stageKey: string; patch: {
  status: StageStatus;
  startDate?: string | null;
  doneDate?: string | null;
} }[] {
  const bay = inferWeldBayKey(spool.stages, jobBay);
  const dense = denseStages(spool.stages, bay, undefined, catalogue);
  const order = catalogue;
  const ops: { stageKey: string; patch: {
    status: StageStatus;
    startDate?: string | null;
    doneDate?: string | null;
  } }[] = [];
  if (targetKey === DONE_KEY) {
    for (const def of order) {
      const st = dense.find((s) => s.stageKey === def.stageKey);
      if (!st || st.status === "skipped") continue;
      if (st.status === "complete") continue;
      ops.push({
        stageKey: def.stageKey,
        patch: {
          status: "complete",
          startDate: st.startDate ?? todayIso,
          doneDate: st.doneDate ?? todayIso,
        },
      });
    }
    return ops;
  }
  if (targetKey === UNSCHEDULED_KEY) {
    for (const def of order) {
      const st = dense.find((s) => s.stageKey === def.stageKey);
      if (!st || st.status === "skipped") continue;
      if (st.status === "pending") continue;
      ops.push({
        stageKey: def.stageKey,
        patch: { status: "pending", doneDate: null },
      });
    }
    return ops;
  }
  const targetIdx = order.findIndex((d) => d.stageKey === targetKey);
  if (targetIdx < 0) return ops;
  for (let i = 0; i < order.length; i += 1) {
    const def = order[i];
    if (!def) continue;
    const st = dense.find((s) => s.stageKey === def.stageKey);
    if (!st || st.status === "skipped") continue;
    if (i < targetIdx) {
      if (st.status === "complete") continue;
      ops.push({
        stageKey: def.stageKey,
        patch: {
          status: "complete",
          startDate: st.startDate ?? todayIso,
          doneDate: st.doneDate ?? todayIso,
        },
      });
    } else if (i === targetIdx) {
      if (st.status === "in-progress") continue;
      ops.push({
        stageKey: def.stageKey,
        patch: {
          status: "in-progress",
          startDate: st.startDate ?? todayIso,
          doneDate: null,
        },
      });
    } else {
      if (st.status === "complete" || st.status === "in-progress") {
        ops.push({
          stageKey: def.stageKey,
          patch: { status: "pending", doneDate: null },
        });
      }
    }
  }
  return ops;
}

export function SpoolBoardView({ jobs, activeOnly, onOpenJob, onSpoolUpdated }: Props) {
  const today = isoToday();
  const catalogue = useActiveStages();
  const stageByKey = useMemo(() => {
    const m = new Map<string, StageDefDto>();
    for (const s of catalogue) m.set(s.stageKey, s);
    return m;
  }, [catalogue]);
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const j of jobs) {
      for (const sp of j.spools) {
        if (activeOnly && isSpoolComplete({ spool: sp, catalogue, jobBay: j.bay })) continue;
        out.push({ job: j, spool: sp });
      }
    }
    return out;
  }, [jobs, activeOnly, catalogue]);
  const columns = useMemo(() => bucket(rows, catalogue), [rows, catalogue]);

  const [dragId, setDragId] = useState<{ jobId: number; spoolId: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onDrop = async (colKey: string) => {
    setDropTarget(null);
    if (!dragId || busy) return;
    const job = jobs.find((j) => j.id === dragId.jobId);
    const spool = job?.spools.find((s) => s.id === dragId.spoolId);
    setDragId(null);
    if (!job || !spool) return;
    const ops = buildAdvancePatches(spool, job.bay, colKey, catalogue, today);
    if (ops.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(
        ops.map((o) => patchSpoolStage(job.id, spool.id, o.stageKey, o.patch)),
      );
      await onSpoolUpdated?.();
    } catch (e) {
      window.alert(`Failed to move spool: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (rows.length === 0) {
    return (
      <div className="bsp-card" style={{ padding: 32, textAlign: "center", color: "var(--bsp-fg-muted)" }}>
        No spools yet — open a job and add spools to populate the board.
      </div>
    );
  }

  return (
    <div className="kanban-wrap">
      <div className="kanban-board">
        {columns.map((col) => {
          const isTarget = dropTarget === col.key && dragId !== null;
          return (
            <div
              key={col.key}
              className={`kanban-col${isTarget ? " is-drop-target" : ""}`}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                if (dropTarget !== col.key) setDropTarget(col.key);
              }}
              onDragLeave={(e) => {
                // Only clear when the drag actually exits the column,
                // not when it crosses over a child element.
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                if (dropTarget === col.key) setDropTarget(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                void onDrop(col.key);
              }}
            >
              <div className="col-header">
                <div className="col-dot" style={{ background: col.color }} />
                <div className="col-title">{col.label}</div>
                <div className="col-count">{col.rows.length}</div>
              </div>
              {col.rows.length === 0 ? (
                <div className="kanban-col-empty">Drop here</div>
              ) : null}
              {col.rows.map((r) => (
                <SpoolCardMini
                  key={`${r.job.id}-${r.spool.id}`}
                  job={r.job}
                  spool={r.spool}
                  today={today}
                  catalogue={catalogue}
                  stageByKey={stageByKey}
                  dragging={dragId?.jobId === r.job.id && dragId?.spoolId === r.spool.id}
                  onDragStart={() => setDragId({ jobId: r.job.id, spoolId: r.spool.id })}
                  onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                  onOpen={() => onOpenJob(r.job.id, r.spool.id)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface MiniProps {
  job: JobDto;
  spool: SpoolDto;
  today: string;
  catalogue: StageDefDto[];
  stageByKey: Map<string, StageDefDto>;
  dragging: boolean;
  onDragStart(): void;
  onDragEnd(): void;
  onOpen(): void;
}

function SpoolCardMini({
  job,
  spool,
  today,
  catalogue,
  stageByKey,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
}: MiniProps) {
  const bay = inferWeldBayKey(spool.stages, job.bay);
  const dense = denseStages(spool.stages, bay, undefined, catalogue);
  const cur = currentStage(dense);
  const curDef = cur ? stageByKey.get(cur.stageKey) : null;
  const active = activeStages(dense);
  const completeCount = active.filter((s) => s.status === "complete").length;
  const pct = active.length > 0 ? Math.round((completeCount / active.length) * 100) : 0;
  const overdue = spool.dueDate && spool.dueDate < today;
  const hours = active.reduce((a, s) => a + (s.hours || 0), 0);

  return (
    <div
      className={`proj-card${dragging ? " is-dragging" : ""}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", `spool:${job.id}:${spool.id}`);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="proj-card-top">
        <span style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, color: "var(--bsp-blue)", fontSize: 12 }}>
          {spool.num}
        </span>
        <span style={{ fontSize: 10, color: "var(--bsp-fg-muted)", fontFamily: "var(--bsp-font-mono)" }}>
          {job.jobNumber}
        </span>
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--bsp-dark)", marginBottom: 4 }}>
        {spool.description || job.name}
      </div>
      {curDef ? (
        <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)", display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
          <span style={{
            display: "inline-block", width: 7, height: 7, borderRadius: "50%",
            background: curDef.color,
          }} />
          {curDef.name}
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <div style={{
          flex: 1,
          height: 4,
          background: "var(--bsp-surface-muted)",
          border: "1px solid var(--bsp-border)",
          borderRadius: 3,
          overflow: "hidden",
        }}>
          <div style={{
            width: `${pct}%`,
            height: "100%",
            background: "linear-gradient(90deg,var(--bsp-blue),var(--bsp-green))",
          }} />
        </div>
        <span style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, fontSize: 10, minWidth: 28, textAlign: "right" }}>{pct}%</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: "var(--bsp-fg-muted)" }}>
        <span style={{ fontFamily: "var(--bsp-font-mono)" }}>{hours.toFixed(1)}h · {completeCount}/{active.length}</span>
        <span style={{
          fontFamily: "var(--bsp-font-mono)",
          color: overdue ? "var(--bsp-red-fg,#B3261E)" : "var(--bsp-fg-muted)",
          fontWeight: overdue ? 700 : 400,
        }}>
          {spool.dueDate ? `Due ${fmtDate(spool.dueDate)}` : "—"}
        </span>
      </div>
    </div>
  );
}
